import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    CHART_COLORS,
    CHART_CONST_INFO,
    CHART_TYPE,
    REPORT_TYPE,
    SEGMENT_BY,
} from '@constants/common/chart/chartConst';
import { formatDate } from '@utils/dates/date.utils';
import { logError } from '@utils/tools/ErrorLoggers';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { configs } from '../../constants/configs/configs';
import BarChartContainer from './BarChartContainer';
import UpcomingDueDate from './UpcomingDueDate';
import useChartInfo from './useChartInfo';
import {
    PERSIST_REPORT_FILTERS_STORAGE_KEY,
    TICKET_LIST_SEARCH_FROM_REPORTS,
    UPCOMING_DUE_DATE_FILTERS_STORAGE_KEY,
} from '../../modules/cap/TicketList/constants/ticketlist.constants';
import {
    getStoredUpcomingDueDateReportFilters,
    isValidDrillDownData,
    type DrillDownFilter,
    type UpcomingDueDatePersistedFilters,
} from './utils/upcomingDueDate.utils';

const CHART_EVENT_TYPES = {
    BAR_CLICK: 'bar',
    AXIS_CLICK: 'hAxis',
    AXIS_LABEL: 'label',
} as const;


function UpcomingDueDateContainer() {
    const navigate = useNavigate();
    const hasInitializedChartSelection = useRef<boolean>(false);
    const shouldKeepRestoredChartOnFirstLoad = useRef<boolean>(false);
    const [serviceResponse, setServiceResponse] = useState<any[]>([]);
    const [defaultReportCoolers, setDefaultReportCoolers] = useState<string[]>(CHART_COLORS.STATUS_REPORTS_COLOR);
    const [showAssigneeSegment, setshowAssigneeSegment] = useState(false);
    const [selectedReport, setSelectedReport] = useState<string>(REPORT_TYPE.UPCOMING_DUE_DATES);
    const [selectedDate, setSelectedDate] = useState<Date>();
    const [selectedChart, setselectedChart] = useState<string>(CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS);
    const [chartData, setchartData] = useState<any[]>([]);
    const [idMapping, setidMapping] = useState<{}[]>([]);
    const [selectedWorkGropeName, setSelectedWorkGropeName] = useState<string>('');
    const [selectedId, setSelectedBarId] = useState<number>();
    const [chartColors, setChartColors] = useState<string[]>(CHART_COLORS.STATUS_REPORTS_COLOR);
    const [isLegendFilterApplied, setIsLegendFilterApplied] = useState<string | null>(null);
    const [isWorkGroupFilterApplied, setIsWorkGroupFilterApplied] = useState<boolean>(false);
    const [legendClassName, setLegendClassName] = useState<string[]>(['na']);
    const [isDrillDownActive, setIsDrillDownActive] = useState<boolean>(false);
    const [isDrillDownAllowed, setIsDrillDownAllowed] = useState<boolean>(true);
    const [segmentBy, setSegmentBy] = useState<string>(SEGMENT_BY.TAB_STATUS);
    const [workGroupFilter, setworkGroupFilter] = useState<string | number | null>(null);
    const [isTagFilterApplied, setIsTagFilterApplied] = useState<boolean>(true);
    const [showBy, setShowBy] = useState<string>(SEGMENT_BY.WORKGROUP);
    const [drillDownData, setDrillDownData] = useState<DrillDownFilter[]>([]);
    const [isFilterStateHydrated, setIsFilterStateHydrated] = useState<boolean>(false);

    const {
        chartInfoData,
        fetchDueDateByWorkGroup,
        startSpinner,
        stopSpinner,
        fetchResolvedDateReportsByWorkGroup,
        fetchResolvedDateReportsGroupByAssigneeForAssigneeIdDrillDown,
        getResolvedDateReportsGroupByAssignee,
        getResolvedDateReportsGroupByAssigneeForAssigneeIdDrillDown,
        getDueDateReportsByWorkGroupTags,
        getDueDateReportsByAssineeTags,
        getOpenTicketReportsByTags,
    } = useChartInfo();

    const getChartColorsInfo = () => {
        if (selectedReport !== REPORT_TYPE.UPCOMING_DUE_DATES) {
            return CHART_COLORS.RESOLVED_DATE_REPORTS_COLOR;
        }
        return segmentBy === SEGMENT_BY.TAB_STATUS
            ? CHART_COLORS.STATUS_REPORTS_COLOR
            : CHART_COLORS.DUEDATE_DATE_REPORTS_COLOR;
    };

    useEffect(() => {
        if (serviceResponse.length > 0) {
            multipleFilterApplied(isLegendFilterApplied, workGroupFilter);
        }
    }, [serviceResponse, isLegendFilterApplied, workGroupFilter]);

    useEffect(() => {
        const parsedFilters = getStoredUpcomingDueDateReportFilters();
        if (!parsedFilters) {
            setIsFilterStateHydrated(true);
            return;
        }
        try {
            const validSegmentByValues = Object.values(SEGMENT_BY);
            const booleanFilterSetters = [
                { value: parsedFilters.isTagFilterApplied, setter: setIsTagFilterApplied },
                { value: parsedFilters.isWorkGroupFilterApplied, setter: setIsWorkGroupFilterApplied },
                { value: parsedFilters.isDrillDownActive, setter: setIsDrillDownActive },
                { value: parsedFilters.isDrillDownAllowed, setter: setIsDrillDownAllowed },
            ];

            booleanFilterSetters.forEach(({ value, setter }) => {
                if (typeof value === 'boolean') {
                    setter(value);
                }
            });

            if (
                typeof parsedFilters.selectedReport === 'string'
                && CHART_CONST_INFO.REPORTS_DROP_DOWN_OPTIONS.includes(parsedFilters.selectedReport)
            ) {
                setSelectedReport(parsedFilters.selectedReport);
            }
            if (
                typeof parsedFilters.segmentBy === 'string'
                && validSegmentByValues.includes(parsedFilters.segmentBy)
            ) {
                setSegmentBy(parsedFilters.segmentBy);
            }
            if (
                typeof parsedFilters.showBy === 'string'
                && validSegmentByValues.includes(parsedFilters.showBy)
            ) {
                setShowBy(parsedFilters.showBy);
            }
            if (parsedFilters.selectedDate) {
                const restoredDate = new Date(parsedFilters.selectedDate);
                if (!Number.isNaN(restoredDate.getTime())) {
                    setSelectedDate(restoredDate);
                }
            }
            if (parsedFilters.workGroupFilter !== undefined && parsedFilters.workGroupFilter !== null) {
                setworkGroupFilter(parsedFilters.workGroupFilter);
            }
            if (parsedFilters.legendFilter) {
                setIsLegendFilterApplied(parsedFilters.legendFilter);
            }
            if (typeof parsedFilters.selectedChart === 'string') {
                setselectedChart(parsedFilters.selectedChart);
                shouldKeepRestoredChartOnFirstLoad.current = true;
            }
            if (typeof parsedFilters.selectedId === 'number') {
                setSelectedBarId(parsedFilters.selectedId);
            }
            if (typeof parsedFilters.selectedWorkGropeName === 'string') {
                setSelectedWorkGropeName(parsedFilters.selectedWorkGropeName);
            }
            if (isValidDrillDownData(parsedFilters.drillDownData)) {
                setDrillDownData(parsedFilters.drillDownData);
            }
        } catch (error) {
            logError('Failed to restore report filters', { error });
        } finally {
            setIsFilterStateHydrated(true);
            sessionStorage.removeItem(UPCOMING_DUE_DATE_FILTERS_STORAGE_KEY);
            sessionStorage.removeItem(PERSIST_REPORT_FILTERS_STORAGE_KEY);
        }
    }, []);

    const persistCurrentFilterState = useCallback(() => {
        const filtersToPersist: UpcomingDueDatePersistedFilters = {
            selectedReport,
            segmentBy,
            showBy,
            isTagFilterApplied,
            selectedDate: selectedDate ? selectedDate.toISOString() : null,
            workGroupFilter,
            isWorkGroupFilterApplied,
            legendFilter: isLegendFilterApplied,
            selectedChart,
            selectedId: selectedId ?? null,
            isDrillDownActive,
            isDrillDownAllowed,
            selectedWorkGropeName,
            drillDownData,
        };
        sessionStorage.setItem(UPCOMING_DUE_DATE_FILTERS_STORAGE_KEY, JSON.stringify(filtersToPersist));
    }, [
        selectedReport,
        segmentBy,
        showBy,
        isTagFilterApplied,
        selectedDate,
        workGroupFilter,
        isWorkGroupFilterApplied,
        isLegendFilterApplied,
        selectedChart,
        selectedId,
        isDrillDownActive,
        isDrillDownAllowed,
        selectedWorkGropeName,
        drillDownData,
    ]);

    useEffect(() => {
        if (!isFilterStateHydrated) {
            return;
        }
        persistCurrentFilterState();
    }, [isFilterStateHydrated, persistCurrentFilterState]);

    useEffect(() => {
        const chartColorsInfo: string[] = getChartColorsInfo();

        if (isLegendFilterApplied && chartData.length > 0) {
            const selectedIndex = serviceResponse[0]?.indexOf(chartData[0][1]);
            switch (segmentBy) {
                case SEGMENT_BY.TAB_STATUS: {
                    setChartColors([CHART_COLORS.STATUS_REPORTS_COLOR[selectedIndex - 1]]);
                    break;
                }
                case SEGMENT_BY.TAB_DUE_DATE: {
                    setChartColors([CHART_COLORS.DUEDATE_DATE_REPORTS_COLOR[selectedIndex - 1]]);
                    break;
                }
                case SEGMENT_BY.ASSIGNEE:
                case SEGMENT_BY.WORKGROUP: {
                    setChartColors([CHART_COLORS.RESOLVED_DATE_REPORTS_COLOR[selectedIndex - 1]]);
                    break;
                }
                default:
                    setChartColors(chartColorsInfo);
                    setDefaultReportCoolers(chartColorsInfo);
                    break;
            }
        } else {
            setChartColors(chartColorsInfo);
            setDefaultReportCoolers(chartColorsInfo);
        }
    }, [chartData]);

    const changeReport = value => {
        clearFilters();
        setchartData([]);
        setServiceResponse([]);
        setSelectedReport(value);
        setIsDrillDownActive(false);
        setIsDrillDownAllowed(true);
        if (value === REPORT_TYPE.UPCOMING_DUE_DATES) {
            setshowAssigneeSegment(false);
            setSegmentBy(SEGMENT_BY.TAB_STATUS);
        } else {
            const activeTabInfo = SEGMENT_BY.WORKGROUP;
            setSegmentBy(activeTabInfo);
        }
    };

    const getWorkNameOrAssigneeName = (selectedWorkGroupFilter: string | number | null): string => {
        let filterItem = '';
        Object.entries(idMapping).forEach((value): void => {
            Object.entries(value[1]).forEach((item): void => {
                if (item[0] === String(selectedWorkGroupFilter)) {
                    filterItem = String(item[1]);
                }
            });
        });
        return filterItem;
    };

    const clearFilters = () => {
        setIsLegendFilterApplied(null);
        setIsWorkGroupFilterApplied(false);
        setworkGroupFilter(null);
        setSelectedBarId(undefined);
        setDrillDownData([]);
        setSelectedWorkGropeName('');
    };

    const applyLegendFilter = legendFilter => {
        const legendFilterData: any = [];
        legendFilterData.push([serviceResponse[0][0], legendFilter]);
        const selectedIndex = serviceResponse[0].indexOf(legendFilter);
        serviceResponse.forEach((rowData, serverIndex) => {
            if (serverIndex > 0) {
                rowData.forEach((element, index) => {
                    if (index === selectedIndex) {
                        legendFilterData.push([rowData[0], element]);
                    }
                });
            }
        });
        return legendFilterData;
    };

    const applyWorkgroupFilter = (workGroupFilter, filterList = serviceResponse) => {
        const filterName = getWorkNameOrAssigneeName(workGroupFilter);
        const workgroupFilteredChartData: any = [];
        workgroupFilteredChartData.push(filterList[0]);
        const data: any = filterList?.filter((value, index) => index > 0 && value[0] === filterName);
        workgroupFilteredChartData.push(...data);
        return workgroupFilteredChartData;
    };

    const multipleFilterApplied = (legendFilter, workGroupFilter) => {
        const legendFilteredData = legendFilter ? applyLegendFilter(legendFilter) : serviceResponse;
        const workgroupFilteredData = workGroupFilter
            ? applyWorkgroupFilter(workGroupFilter, legendFilteredData)
            : legendFilteredData;
        setchartData(workgroupFilteredData);
    };

    const filterBasedOnLegend = (e: React.MouseEvent<HTMLAnchorElement>, legendFilter: string) => {
        const legendIndex = e.currentTarget.getAttribute('data-legend-index');
        const indexId = legendIndex !== null ? Number(legendIndex) : -1;
        if (indexId < 0) {
            return;
        }
        if (legendClassName[indexId] === '--clickable') {
            setIsLegendFilterApplied(legendFilter);
            const legendClassNameUpdated = legendClassName.map((_item, index) =>
                index === indexId ? '--clicked' : '--clickable',
            );
            setLegendClassName(legendClassNameUpdated);
            if (isWorkGroupFilterApplied) {
                multipleFilterApplied(legendFilter, workGroupFilter);
            } else {
                const legendFilterData = applyLegendFilter(legendFilter);
                setchartData(legendFilterData);
            }
        } else {
            const legendClassNameUpdated = legendClassName.map((item, index) =>
                index === indexId ? '--clickable' : item,
            );
            setLegendClassName(legendClassNameUpdated);
            setIsLegendFilterApplied(null);
            multipleFilterApplied('', workGroupFilter);
        }
    };

    const filterBySelectedWorkGroup = workGroupFilter => {
        if (workGroupFilter === null || workGroupFilter === '') {
            setworkGroupFilter(null);
            setchartData(serviceResponse);
        } else {
            setworkGroupFilter(workGroupFilter);
            setIsWorkGroupFilterApplied(true);
        }
    };

    const onSelectedDate = date => {
        setworkGroupFilter(null);
        setIsLegendFilterApplied(null);
        setSelectedDate(date);
        fetchDataFromApi(selectedChart, selectedId, date);
    };

    const loadChartData = response => {
        stopSpinner();
        const formattedData: any[] = [];
        const idMapping = response?.responseBody?.idMapping;
        const category = response?.responseBody?.category;
        const responseData = response?.responseBody?.responseData;
        if (responseData) {
            formattedData.push(category);
            responseData.forEach((data: {}) => {
                const responseArray: any[] = [];
                idMapping.forEach((item: object) => {
                    const value = item[Number(Object.keys(data)[0])];
                    if (value) {
                        responseArray.push(value);
                    }
                });
                responseArray.push(...data[Object.keys(data)[0]]);
                formattedData.push(responseArray);
            });
            const numberOfLegendClass = formattedData
                ?.filter((v, i) => i === 0)[0]
                .filter((v, i) => i > 0)
                .map(v => '--clickable');
            setLegendClassName(numberOfLegendClass);
            setServiceResponse(formattedData);
            setidMapping(idMapping);
        }

        if (selectedReport && selectedReport === REPORT_TYPE.UPCOMING_DUE_DATES) {
            setSelectedDate(selectedDate =>
                response?.resultDate ? new Date(response?.resultDate) : selectedDate ? selectedDate : new Date(),
            );
        }
    };

    const fetchDataFromApi = (
        selectedChart: string,
        selectedId?: number | null,
        selectedDate?: any,
        selectedReport: any = REPORT_TYPE.UPCOMING_DUE_DATES,
    ) => {
        startSpinner();
        setchartData([]);
        setServiceResponse([]);
        let payload = {};
        let actionCall;

        switch (selectedChart) {
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_STATUS:
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE:
            case CHART_TYPE.ACTIVATE_STATUS_BY_USERS:
            case CHART_TYPE.ACTIVATE_DUEDATE_BY_USERS:
                payload = {
                    activeChart: selectedChart || '',
                    workgroup: selectedId || '',
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    wokGroupDueDateReport: showBy === SEGMENT_BY.WORKGROUP,
                };
                actionCall = fetchDueDateByWorkGroup;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN_USER:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN_USER:
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS:
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE:
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS_DRILLDOWN:
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE_DRILLDOWN:
                payload = {
                    activeChart: selectedChart || '',
                    userId: selectedId || '',
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    wokGroupDueDateReport: showBy === SEGMENT_BY.WORKGROUP,
                };
                actionCall = fetchDueDateByWorkGroup;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS_DRILL_DOWN:
                payload = {
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    tagId: selectedId,
                };
                actionCall = getDueDateReportsByWorkGroupTags;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN:
                payload = {
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    tagId: selectedId,
                };
                actionCall = getDueDateReportsByAssineeTags;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE:
                payload = {
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    tagId: selectedId,
                    wokGroupDueDateReport: showBy === SEGMENT_BY.WORKGROUP,
                    tagSwitchOn: isTagFilterApplied,
                };
                actionCall = getOpenTicketReportsByTags;
                break;
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE_DRILL_DOWN:
                payload = {
                    dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                    tagId: selectedId,
                    wokGroupDueDateReport: showBy === SEGMENT_BY.WORKGROUP,
                };
                actionCall = getOpenTicketReportsByTags;
                break;

            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_DRILL_DOWN:
                payload = { tagSwitchOn: isTagFilterApplied, workGroupId: selectedId };
                actionCall = fetchResolvedDateReportsGroupByAssigneeForAssigneeIdDrillDown;
                break;
            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_DRILL_DOWN:
                payload = { tagSwitchOn: isTagFilterApplied, assignee: selectedId };
                actionCall = getResolvedDateReportsGroupByAssigneeForAssigneeIdDrillDown;
                break;
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY:
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_TAG_DUEDATE_RESOLVED_TODAY:
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_TAG_DRILL_DOWN:
                payload = { tagSwitchOn: isTagFilterApplied, tagId: selectedId };
                actionCall = fetchResolvedDateReportsByWorkGroup;
                break;

            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_TAG_DRILL_DOWN:
                payload = { tagSwitchOn: isTagFilterApplied, tagId: selectedId };
                actionCall = getResolvedDateReportsGroupByAssignee;
                break;

            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE:
            case CHART_TYPE.RESOLVED_DATE_REPORTS_TAG_GROUP_BY_ASSIGNEE:
                payload = { tagSwitchOn: isTagFilterApplied, tagId: selectedId };
                actionCall = getResolvedDateReportsGroupByAssignee;
                break;
            default:
                break;
        }
        setidMapping([]);
        actionCall(payload).then(response => {
            setshowAssigneeSegment(response?.showAssigneeSegment);
            loadChartData(response);
        });
    };

    const backToDetailedReport = useCallback(() => {
        const previousDrillDownData = drillDownData[drillDownData.length - 1];
        if (!previousDrillDownData) {
            return;
        }
        const nextDrillDownData = drillDownData.slice(0, -1);
        const lastDrillDownData = nextDrillDownData[nextDrillDownData.length - 1];
        setDrillDownData(nextDrillDownData);
        setIsDrillDownActive(nextDrillDownData.length > 0);
        setIsDrillDownAllowed(true);
        setSelectedWorkGropeName(lastDrillDownData?.selectedWorkGropeName || '');
        setSelectedBarId(lastDrillDownData?.selectedId ?? undefined);
        setselectedChart(previousDrillDownData.selectedChart);
        setworkGroupFilter(previousDrillDownData.selectedWorkgroup ?? null);
        setIsLegendFilterApplied(null);
    }, [drillDownData]);

    const getFilter = parts => {
        const isTodayActivityReport = REPORT_TYPE.UPCOMING_DUE_DATES !== selectedReport;
        const isDueDateReport = SEGMENT_BY.TAB_DUE_DATE === segmentBy;

        if (isLegendFilterApplied) {
            const filterIndex = legendClassName.map(v => v === '--clicked').indexOf(true);
            if (isTodayActivityReport) {
                return CHART_CONST_INFO.TODAYS_ACTIVITY_LIST[filterIndex];
            } else if (isDueDateReport) {
                return CHART_CONST_INFO.DUEDATE_FILTER_LIST[filterIndex];
            } else {
                return CHART_CONST_INFO.STATUS_FILTER_LIST[filterIndex];
            }
        }

        const filterIndex = Number(parts[1]);

        if (isTodayActivityReport) {
            return CHART_CONST_INFO.TODAYS_ACTIVITY_LIST[filterIndex];
        } else if (isDueDateReport) {
            return CHART_CONST_INFO.DUEDATE_FILTER_LIST[filterIndex];
        } else {
            return CHART_CONST_INFO.STATUS_FILTER_LIST[filterIndex];
        }
    };

    const getSelectedBarId = (parts: string[]): number | string => {
        const selectedBarIndex = Number(parts[2]);
        if (selectedBarIndex >= 0 && selectedBarIndex < idMapping.length) {
            const selectedGroup = idMapping[selectedBarIndex] || {};
            return Number(Object.keys(selectedGroup)[0]);
        }
        return '';
    };

    const getFilteredIdOrDefault = (parts: string[]): number | string => {
        if (isWorkGroupFilterApplied && workGroupFilter) {
            return workGroupFilter;
        }
        return getSelectedBarId(parts);
    };

    const toUserChart = e => {
        const isResolvedTodayReport: boolean = selectedReport !== REPORT_TYPE.UPCOMING_DUE_DATES;
        const parts = e.targetID.split('#');
        const wokGroupDueDateReport =
            selectedReport === REPORT_TYPE.UPCOMING_DUE_DATES
                ? showBy === SEGMENT_BY.WORKGROUP
                : segmentBy === SEGMENT_BY.WORKGROUP;
        if (parts[0] === CHART_EVENT_TYPES.BAR_CLICK) {
            if (parts.length < 3) {
                return;
            }
            let workgroupId: number | string = '';
            if (getTitle === SEGMENT_BY.WORKGROUP) {
                workgroupId = getFilteredIdOrDefault(parts);
            } else {
                workgroupId = getParentId(drillDownData, true);
            }

            let assigneeId: number | string = '';
            if (getTitle === SEGMENT_BY.USER || getTitle === SEGMENT_BY.ASSIGNEE) {
                assigneeId = getFilteredIdOrDefault(parts);
            } else {
                assigneeId = getParentId(drillDownData, false);
            }
            let tagId: number | string = '';
            if (getTitle === SEGMENT_BY.TAG) {
                tagId = getFilteredIdOrDefault(parts);
            }

            const ticketListSortFromChart = {
                dueReport: segmentBy === SEGMENT_BY.TAB_DUE_DATE ? 'yes' : 'no',
                pageNumber: 1,
                pageSize: 10,
                sortOrder: 'DESC',
                sortBy: 'assignee',
                workgroupId,
                filter: getFilter(parts),
                assigneeId,
                tagId,
                dueDate: selectedDate ? formatDate(new Date(selectedDate), MM_SLASH_DD_SLASH_YYYY) : '',
                resolvedReport: isResolvedTodayReport ? true : false,
                wokGroupDueDateReport,
            };

            startSpinner();
            try {
                persistCurrentFilterState();
                const chartSortPayload = JSON.stringify(ticketListSortFromChart);
                sessionStorage.setItem(TICKET_LIST_SEARCH_FROM_REPORTS, chartSortPayload);
                navigate(configs.routes.TICKET_LIST + '?dueDateReport=true');
            } catch (error) {
                logError('Failed to navigate to ticket list', { error });
                stopSpinner();
            }
        }
        if (isDrillDownAllowed && parts[0] === CHART_EVENT_TYPES.AXIS_CLICK) {
            clearFilters();

            if (parts.indexOf(CHART_EVENT_TYPES.AXIS_LABEL) >= 0) {
                let idx = parts[parts.indexOf(CHART_EVENT_TYPES.AXIS_LABEL) + 1];
                idx = parseInt(idx);
                setSelectedWorkGropeName(chartData[idx + 1][0]);
                const selectedId: number = Number(
                    Object.keys(idMapping?.filter((acc: {}) => Object.values(acc)[0] === chartData[idx + 1][0])[0])[0],
                );
                setDrillDownChart(selectedId);
                setIsDrillDownActive(true);
                setDrillDownData([
                    ...drillDownData,
                    {
                        selectedId,
                        selectedWorkGropeName: chartData[idx + 1][0],
                        selectedChart,
                        selectedWorkgroup: workGroupFilter,
                        type: getTitle,
                    },
                ]);
            }
        }
    };

    const setDrillDownChart = id => {
        setSelectedBarId(id);
        startSpinner();

        let newChart = selectedChart;
        let drilldownAllowed = false;

        switch (selectedChart) {
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE_DRILL_DOWN;
                drilldownAllowed = true;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS_DRILLDOWN;
                drilldownAllowed = false;
                break;
            case CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE_DRILLDOWN;
                drilldownAllowed = false;
                break;
            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN;
                drilldownAllowed = true;
                break;
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS_DRILL_DOWN;
                drilldownAllowed = true;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN;
                drilldownAllowed = true;
                break;
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE_DRILL_DOWN:
                newChart = CHART_TYPE.ACTIVATE_DUEDATE_BY_USERS;
                drilldownAllowed = false;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE_DRILL_DOWN_USER;
                drilldownAllowed = false;
                break;

            case CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN:
                newChart = CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS_DRILL_DOWN_USER;
                drilldownAllowed = false;
                break;

            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_STATUS:
            case CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS_DRILL_DOWN:
                newChart = CHART_TYPE.ACTIVATE_STATUS_BY_USERS;
                drilldownAllowed = false;
                break;

            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_TAG_DUEDATE_RESOLVED_TODAY:
                newChart = CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_TAG_DRILL_DOWN;
                drilldownAllowed = true;
                break;

            case CHART_TYPE.RESOLVED_DATE_REPORTS_TAG_GROUP_BY_ASSIGNEE:
                newChart = CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_TAG_DRILL_DOWN;
                drilldownAllowed = true;
                break;

            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE:
            case CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_TAG_DRILL_DOWN:
                newChart = CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE_FOR_ASSIGNEEID_DRILL_DOWN;
                drilldownAllowed = false;
                break;
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_TAG_DRILL_DOWN:
            case CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY:
                newChart = CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY_ASSIGNEE_DRILL_DOWN;
                drilldownAllowed = false;
                break;

            default:
                break;
        }
        setselectedChart(newChart);
        setIsDrillDownAllowed(drilldownAllowed);
    };

    useEffect(() => {
        if (serviceResponse.length === 0 || !isLegendFilterApplied) {
            return;
        }
        const selectedLegendIndex = serviceResponse[0].indexOf(isLegendFilterApplied) - 1;
        if (selectedLegendIndex < 0) {
            return;
        }
        setLegendClassName(currentLegendClass =>
            currentLegendClass.map((_legendClass, index) =>
                index === selectedLegendIndex ? '--clicked' : '--clickable',
            ),
        );
    }, [serviceResponse, isLegendFilterApplied]);

    useEffect(() => {
        if (!isFilterStateHydrated) {
            return;
        }
        const determineChartType = () => {
            if (selectedReport === REPORT_TYPE.UPCOMING_DUE_DATES) {
                if (isTagFilterApplied) {
                    if (showBy === SEGMENT_BY.WORKGROUP) {
                        return segmentBy === SEGMENT_BY.TAB_DUE_DATE
                            ? CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_DUE_DATE
                            : CHART_TYPE.DUE_DATE_REPORTS_BY_WORKGROUP_TAGS_STATUS;
                    } else {
                        return segmentBy === SEGMENT_BY.TAB_DUE_DATE
                            ? CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_DUE_DATE
                            : CHART_TYPE.DUE_DATE_REPORTS_BY_ASSIGNEE_TAGS_STATUS;
                    }
                } else {
                    if (showBy === SEGMENT_BY.WORKGROUP) {
                        return segmentBy === SEGMENT_BY.TAB_DUE_DATE
                            ? CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE
                            : CHART_TYPE.ACTIVATE_WORKGROUP_BY_STATUS;
                    } else {
                        return segmentBy === SEGMENT_BY.TAB_DUE_DATE
                            ? CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_DUE_DATE
                            : CHART_TYPE.DUE_DATE_REPORTS_ASSIGNEE_STATUS;
                    }
                }
            } else {
                if (isTagFilterApplied) {
                    return segmentBy === SEGMENT_BY.WORKGROUP
                        ? CHART_TYPE.ACTIVATE_WORKGROUP_BY_TAG_DUEDATE_RESOLVED_TODAY
                        : CHART_TYPE.RESOLVED_DATE_REPORTS_TAG_GROUP_BY_ASSIGNEE;
                } else {
                    return segmentBy === SEGMENT_BY.WORKGROUP
                        ? CHART_TYPE.ACTIVATE_WORKGROUP_BY_DUEDATE_RESOLVED_TODAY
                        : CHART_TYPE.RESOLVED_DATE_REPORTS_GROUP_BY_ASSIGNEE;
                }
            }
        };

        if (!hasInitializedChartSelection.current) {
            hasInitializedChartSelection.current = true;
            if (shouldKeepRestoredChartOnFirstLoad.current) {
                shouldKeepRestoredChartOnFirstLoad.current = false;
                return;
            }
        }

        setselectedChart(determineChartType());
        setworkGroupFilter(null);
        setIsLegendFilterApplied(null);
    }, [isTagFilterApplied, segmentBy, showBy, isFilterStateHydrated]);

    useEffect(() => {
        if (!isFilterStateHydrated) {
            return;
        }
        fetchDataFromApi(selectedChart, selectedId, selectedDate);
    }, [selectedChart, isFilterStateHydrated]);

    const onTagFilterToggle = isEnabled => {
        setIsDrillDownAllowed(true);
        setIsTagFilterApplied(isEnabled);
        setDrillDownData([]);
    };

    const getTitle = useMemo(() => {
        const title = chartData?.[0]?.[0] || '';
        return String(title).charAt(0).toUpperCase() + String(title).slice(1);
    }, [chartData]);

    const getParentId = useCallback((drillDownData, getWorkGroupId) => {
        const workgroupData = drillDownData.find((drillData: { type: string; selectedId: number }) => {
            if (getWorkGroupId) {
                return drillData.type === SEGMENT_BY.WORKGROUP;
            } else {
                return drillData.type === SEGMENT_BY.ASSIGNEE || drillData.type === SEGMENT_BY.USER;
            }
        });
        return workgroupData?.selectedId || '';
    }, []);

    return (
        <>
            <UpcomingDueDate
                segmentBy={segmentBy}
                setSegmentBy={setSegmentBy}
                selectedReport={selectedReport}
                RenderByWorkGroup={
                    <BarChartContainer
                        selectedReport={selectedReport}
                        selectedDate={selectedDate ?? new Date()}
                        onSelectedDate={onSelectedDate}
                        isDrillDownActive={isDrillDownActive}
                        backToDetailedReport={backToDetailedReport}
                        selectedWorkGropeName={selectedWorkGropeName}
                        chartData={chartData}
                        selectedChart={selectedChart}
                        chartColors={chartColors}
                        toUserChart={toUserChart}
                        serviceResponse={serviceResponse}
                        legendClassName={legendClassName}
                        filterBasedOnLegend={filterBasedOnLegend}
                        defaultReportCoolers={defaultReportCoolers}
                        isDrillDownAllowed={isDrillDownAllowed}
                        isLoading={chartInfoData.isSpinning}
                        title={getTitle}
                    />
                }
                changeReport={changeReport}
                showAssigneeSegment={showAssigneeSegment}
                idMapping={idMapping}
                filterBySelectedWorkGroup={filterBySelectedWorkGroup}
                isDrillDownActive={isDrillDownActive}
                isTagFilterApplied={isTagFilterApplied}
                setIsTagFilterApplied={onTagFilterToggle}
                workGroupFilter={workGroupFilter}
                selectedChart={selectedChart}
                showBy={showBy}
                setShowBy={setShowBy}
                title={getTitle}
            />
        </>
    );
}
export default UpcomingDueDateContainer;
